// God Mode — a short, cosmetic-only easter-egg session (triggered by the "iddqd" code). Nothing here
// touches permissions or real data; it just flips a client-side flag the header ⚡ and dashboard read.
// The session lasts a random 2–4 minutes and then expires on its own (see EXPIRED_EVENT consumers).
const EVENT = 'god_mode_changed';
const EXPIRED_EVENT = 'god_mode_expired';

// Random session length. A single constant — bump the range to let God Mode run longer.
export const DURATION_MS = { min: 120_000, max: 240_000 };

export interface GodModeEntry {
    label: string;
    value: string;
}

export interface GodModeSession {
    powers: GodModeEntry[];
}

const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

// Universal powers — funny for everyone (sales, call center, marketing, devs), shown every time.
const universalPowers = (): GodModeEntry[] => [
    { label: 'Confidence', value: pick(['MAXIMUM', 'UNSHAKEABLE', 'OVER 9000']) },
    { label: 'Luck', value: `+${rand(12, 99)}%` },
    { label: 'Coffee Level', value: `${pick(['OPTIMAL', 'RISING', 'CRITICAL'])} ☕` },
    { label: 'Monday Resistance', value: pick(['ACTIVE', 'HOLDING', 'MAXIMUM']) },
];

// One extra "wildcard" power, picked at random each activation and tailored to the logged-in user's
// department — so sales sees sales jokes, IT sees IT jokes, etc. — making God Mode feel like it keeps
// discovering ever more ridiculous, personally-relevant powers.
type Wildcard = () => GodModeEntry;

const GENERIC_WILDCARDS: Wildcard[] = [
    () => ({ label: 'Inbox zero', value: 'ACHIEVED' }),
    () => ({ label: 'Deadline elasticity', value: 'INFINITE' }),
    () => ({ label: 'Coffee radar', value: 'ACTIVE' }),
    () => ({ label: 'Small talk', value: 'MASTERED' }),
];

// Keyed by t_administrator_department.ID: 1 management, 2 marketing, 3 validation, 4 sales, 5 IT.
// (10 external and any unknown fall back to GENERIC_WILDCARDS.)
const DEPARTMENT_WILDCARDS: Record<number, Wildcard[]> = {
    1: [
        () => ({ label: 'Meeting escape velocity', value: 'REACHED' }),
        () => ({ label: 'Strategic vision', value: '20/20' }),
        () => ({ label: 'Budget elasticity', value: 'INFINITE' }),
        () => ({ label: 'Reply-All immunity', value: 'ACTIVE' }),
    ],
    2: [
        () => ({ label: 'Excel mastery', value: `+${rand(40, 98)}%` }),
        () => ({ label: 'Campaign ROI', value: 'OVER 9000' }),
        () => ({ label: 'Brand aura', value: 'RADIANT' }),
        () => ({ label: 'Viral potential', value: 'MAXIMUM' }),
    ],
    3: [
        () => ({ label: 'Fraud radar', value: 'CALIBRATED' }),
        () => ({ label: 'Spam resistance', value: 'MAXIMUM' }),
        () => ({ label: 'Attention to detail', value: '20/20' }),
        () => ({ label: 'Duplicate detection', value: 'INSTANT' }),
    ],
    4: [
        () => ({ label: 'Cold-call charisma', value: 'MAXIMUM' }),
        () => ({ label: 'Sales rejection', value: 'IMMUNE' }),
        () => ({ label: 'Closing power', value: 'MAXIMUM' }),
        () => ({ label: 'Follow-up energy', value: 'INFINITE' }),
    ],
    5: [
        () => ({ label: 'Immunity to NullPointerException', value: 'ACTIVE' }),
        () => ({ label: 'Ability to reproduce the bug', value: 'DENIED' }),
        () => ({ label: 'Friday deployments', value: 'LOCKED 🔒' }),
        () => ({ label: 'Merge conflict resolution', value: 'INSTANT' }),
    ],
};

// Rolled fresh on every activation, so the powers change each time God Mode is entered.
function rollSession(departmentId?: number): GodModeSession {
    const pool = (departmentId != null && DEPARTMENT_WILDCARDS[departmentId]) || GENERIC_WILDCARDS;
    return { powers: [...universalPowers(), pick(pool)()] };
}

let session: GodModeSession | null = null;
let expiresAt = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

export const godModeStore = {
    isActive: (): boolean => session !== null && Date.now() < expiresAt,
    getSession: (): GodModeSession | null => session,
    getExpiresAt: (): number => expiresAt,

    activate: (departmentId?: number): void => {
        session = rollSession(departmentId);
        const duration = rand(DURATION_MS.min, DURATION_MS.max);
        expiresAt = Date.now() + duration;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => godModeStore.expire(), duration);
        window.dispatchEvent(new CustomEvent(EVENT));
    },

    expire: (): void => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        session = null;
        expiresAt = 0;
        window.dispatchEvent(new CustomEvent(EVENT));
        window.dispatchEvent(new CustomEvent(EXPIRED_EVENT));
    },

    // Fires on any activate/expire — for the header ⚡ show/hide.
    subscribe: (cb: () => void): (() => void) => {
        window.addEventListener(EVENT, cb);
        return () => window.removeEventListener(EVENT, cb);
    },

    // Fires only when a session ends — for the "you are mortal again" message + Mere Mortal achievement.
    onExpire: (cb: () => void): (() => void) => {
        window.addEventListener(EXPIRED_EVENT, cb);
        return () => window.removeEventListener(EXPIRED_EVENT, cb);
    },
};
